import Close from '@ixigo/icons/Close';
import FlightTakeoff from '@ixigo/icons/FlightTakeoff';
import LocationOn from '@ixigo/icons/LocationOn';
import Button from '@ixigo/iui/Button';
import IconButton from '@ixigo/iui/IconButton';
import ListItemText from '@ixigo/iui/ListItemText';
import Typography from '@ixigo/iui/Typography';
import { Search } from '@repo/shared/components';
import { Loader } from '@repo/shared/icons';
import cn from '@repo/shared/utils/cn';
import { getLocations, getPlaceDetails } from '../autocomplete/api';
import { Combobox } from '../autocomplete/Combobox';
import type { LocationSuggestion } from '../autocomplete/types';
import {
	type UseComboboxParams,
	useCombobox,
} from '../autocomplete/useCombobox';
import type { MapLocation } from '../types';

type MapLocationAutocompleteProps = {
	/**
	 * If true, the autocomplete will be expanded
	 */
	open?: boolean;

	onOpenChange: (open: boolean) => void;

	value?: MapLocation;

	/**
	 * when value is selected from autocomplete
	 */
	onChange: (location: MapLocation) => void;

	/**
	 * when value is confirmed
	 */
	onConfirm: (location: MapLocation) => void;

	className?: string;

	title: string;
};

const loadOptions = {
	debounceMs: 300,
	minQueryLength: 2,
} satisfies UseComboboxParams<unknown>['loadOptions'];

function hasCoords(value: LocationSuggestion): value is MapLocation {
	return !!(value.lat && value.lng);
}

const tempControlledValue: LocationSuggestion = {
	mainText: '',
	formattedAddress: '',
};

export function MapLocationAutocomplete({
	open: isExpanded,
	value,
	onOpenChange,
	onChange,
	onConfirm,
	className,
	title,
}: MapLocationAutocompleteProps) {
	const onItemSelect = (options: { value: LocationSuggestion }) => {
		onOpenChange(false);
		if (hasCoords(options.value)) {
			onChange(options.value);
		} else if (options.value.placeId) {
			// fetch place details and then call onChange
			getPlaceDetails(options.value.placeId).then((place) => {
				place && onChange(place);
			});
		}
	};
	const combobox = useCombobox({
		load: getLocations,
		itemToString: (item) => item.mainText,
		onChange: onItemSelect,
		loadOptions,
		value: (value as LocationSuggestion) || tempControlledValue,
	});
	const { items, getListProps, getItemProps } = combobox;

	const bottomContentClassName = cn(
		'rounded-t-30 shadow-500 relative bg-primary flex flex-col z-[1]',
		isExpanded ? 'flex-[8] overflow-hidden' : 'flex-[0]',
		className,
	);

	const inputProps = () => {
		const endAdornment = combobox.loading ? (
			<Loader className="text-selection icon-md" />
		) : (
			<Combobox.ClearButton {...combobox.getClearProps()} />
		);
		const props = {
			...combobox.getInputProps({
				onFocus: () => onOpenChange(true),
			}),
		};
		if (isExpanded) {
			props.endAdornment = endAdornment;
		}
		return props;
	};

	return (
		<div
			className={bottomContentClassName}
			style={{
				viewTransitionName: 'bottom-content',
			}}
		>
			{/* Header */}
			<div className="pb-2.5 pt-15px text-center px-20">
				<Typography
					variant="h5"
					fontWeight="bold"
					className="relative flex items-center justify-center"
				>
					{isExpanded && (
						<IconButton
							className="absolute -left-1.5"
							onClick={() => onOpenChange(false)}
						>
							<Close />
						</IconButton>
					)}
					{title}
				</Typography>
				{!isExpanded && (
					<Typography className="text-secondary">
						Drag the map to move the pin
					</Typography>
				)}
			</div>
			{/* Separator */}
			<div className="border-t border-tertiary" />
			{/* Body */}
			<div className="pt-20 pb-2.5 px-20 flex-1 flex flex-col overflow-hidden">
				<Search
					fullWidth
					placeholder="Search for a location"
					{...inputProps()}
				/>
				{isExpanded && (
					<div {...getListProps()} className="flex-1 overflow-y-auto">
						{items.map((s, index) => (
							<Combobox.Item
								key={`${s.mainText}-${index}`}
								{...getItemProps({ item: s })}
							>
								<Combobox.Icon>
									{s.type === 'airport' ? <FlightTakeoff /> : <LocationOn />}
								</Combobox.Icon>
								<ListItemText
									primary={s.mainText}
									secondary={s.secondaryText}
								/>
							</Combobox.Item>
						))}
					</div>
				)}
			</div>
			{/* Footer */}
			<div className={cn('py-2.5 px-20', isExpanded && 'hidden')}>
				<Button
					variant="solid"
					size="lg"
					className="rounded-full w-full"
					disabled={!value}
					onClick={() => value && onConfirm(value)}
				>
					Confirm Location
				</Button>
			</div>
		</div>
	);
}
